import axios from 'axios';
import { useEffect, useReducer, useRef } from 'react';

import { useAlert } from './useAlert';
import {
  sendLogRest,
  ServiceUrl,
} from '@portal/lib';
import { locale } from '@portal/locale';

const DefaultState = {
  data: null,
  error: null,
  isLoading: false,
};

const Action = {
  Start: 'loading',
  Success: 'fetched',
  Failure: 'error',
};

const fetchReducer = (state, action) => {
  switch (action.type) {
    case Action.Start:
      return {
        ...DefaultState,
        isLoading: true,
      };
    case Action.Success:
      return {
        ...DefaultState,
        data: action.payload,
        isLoading: false,
      };
    case Action.Failure:
      return {
        ...DefaultState,
        error: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

/** Perform standard http requests */
export const useFetch = ({
  baseURL,
  errorText,
  isErrorAlertMuted = false,
  onError,
  onSuccess,
} = {}) => {
  const [state, dispatch] = useReducer(fetchReducer, DefaultState);
  const { setErrorAlert } = useAlert();

  // Used to prevent state update if the component is unmounted
  const cancelRequest = useRef(false);

  useEffect(() => {
    // Use the cleanup function for avoiding a possibly state update after the component was unmounted
    return () => {
      cancelRequest.current = true;
    };
  }, []);

  const handleError = (error) => {
    const shouldMute = isErrorAlertMuted;
    const errorMessage = error?.response?.data?.message
      ?.toLocaleString?.()
      .toLowerCase?.();
    const shouldUsePhoneNumberError = errorMessage?.includes(
      locale.validatePhoneNumberMessage
    );

    if (!shouldMute) {
      const message = shouldUsePhoneNumberError
        ? locale.validatePhoneNumberErrorMessage
        : errorText;
      setErrorAlert(message);
    }

    onError?.(error);
    sendLogRest({
      message: JSON.stringify(error),
      error,
    });
  };

  const fetchData = async (config) => {
    cancelRequest.current = false;

    dispatch({ type: Action.Start });

    try {
      const isRestApiRequest =
        config?.url?.includes(ServiceUrl) ||
        baseURL?.includes(ServiceUrl);
      const restApiHeaders = isRestApiRequest
        ? { 'Client-Name': 'dealer-portal' }
        : {};
      const response = await axios({
        baseURL,
        ...config,
        headers: {
          ...restApiHeaders,
          ...config?.headers,
        },
      });

      if (cancelRequest.current) {
        return;
      }

      dispatch({ type: Action.Success, payload: response.data });

      onSuccess?.(response.data);

      return response.data;
    } catch (error) {
      onError?.(error);
      if (cancelRequest.current) {
        return;
      }

      dispatch({ type: Action.Failure, payload: error });

      handleError(error);
    }
  };

  return [fetchData, state];
};
